import api from './api';

// AI service for help chat management and analysis

export interface ChatLog {
  id: number;
  user_email: string;
  question: string;
  answer: string | null;
  tools_used: Array<{ tool: string; input: any }>;
  success: boolean;
  error_message: string | null;
  page_context: string | null;
  response_time_ms: number | null;
  created_at: string;
}

export interface ChatLogsResponse {
  logs: ChatLog[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface QuestionTheme {
  theme: string;
  count: number;
  example_questions: string[];
}

export interface Suggestion {
  type: 'endpoint' | 'documentation' | 'tool' | 'enhancement';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  implementation_notes: string;
  questions_addressed: string[];
}

export interface AnalysisResponse {
  failures_analyzed: number;
  top_question_themes: QuestionTheme[];
  suggestions: Suggestion[];
  summary: string;
  message?: string;
  parse_error?: boolean;
}

export interface ChatLogsParams {
  limit?: number;
  offset?: number;
  success_filter?: 'true' | 'false' | null;
  user_email_filter?: string;
}

export interface SavedSuggestion {
  id: number;
  type: 'endpoint' | 'documentation' | 'tool' | 'enhancement';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string | null;
  implementation_notes: string | null;
  questions_addressed: string[];
  status: 'pending' | 'in_progress' | 'completed' | 'dismissed';
  completed_at: string | null;
  completed_by: string | null;
  completion_notes: string | null;
  created_at: string | null;
}

export interface CompletedImprovement {
  id: number;
  type: string;
  priority: string;
  title: string;
  description: string | null;
  completion_notes: string | null;
  completed_at: string | null;
  completed_by: string | null;
  questions_addressed: string[];
  user_asked: boolean;
}

class AIService {
  // Note: AI router is mounted without prefix, so endpoints are directly under /help-chat
  async getChatLogs(params: ChatLogsParams = {}): Promise<ChatLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.success_filter) queryParams.append('success_filter', params.success_filter);
    if (params.user_email_filter) queryParams.append('user_email_filter', params.user_email_filter);

    const queryString = queryParams.toString();
    const url = `/help-chat/logs${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<ChatLogsResponse>(url);
    return response.data;
  }

  async analyzeFailures(): Promise<AnalysisResponse> {
    const response = await api.post<AnalysisResponse>(`/help-chat/analyze-failures`);
    return response.data;
  }

  async getHelpChatStatus(): Promise<{
    status: string;
    pinecone_configured: boolean;
    openai_configured: boolean;
    anthropic_configured: boolean;
    index_exists: boolean;
    document_count: number;
  }> {
    const response = await api.get(`/help-chat/status`);
    return response.data;
  }

  async uploadDocument(file: File, pageName: string): Promise<{
    status: string;
    filename: string;
    page_name: string;
    chunks_indexed: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('page_name', pageName);

    const response = await api.post('/help-chat/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getIndexedDocuments(): Promise<{
    total_vectors: number;
    index_name: string;
    dimension: number;
    namespaces: Record<string, any>;
    pages: Array<{
      page: string;
      vector_count: number;
      sample_sections: string[];
    }>;
  }> {
    const response = await api.get('/help-chat/indexed-documents');
    return response.data;
  }

  async deletePage(pageName: string): Promise<{
    status: string;
    page_name: string;
    vectors_deleted: number;
  }> {
    const response = await api.delete(`/help-chat/delete-page/${encodeURIComponent(pageName)}`);
    return response.data;
  }

  async clearIndex(): Promise<{ status: string; message: string }> {
    const response = await api.delete('/help-chat/clear-index');
    return response.data;
  }

  // AI Improvement Suggestions
  async saveSuggestion(suggestion: {
    type: string;
    priority: string;
    title: string;
    description?: string;
    implementation_notes?: string;
    questions_addressed?: string[];
  }): Promise<{ status: string; id: number }> {
    const response = await api.post('/suggestions', suggestion);
    return response.data;
  }

  async getSuggestions(status?: string): Promise<{
    suggestions: SavedSuggestion[];
    count: number;
  }> {
    const url = status ? `/suggestions?status=${status}` : '/suggestions';
    const response = await api.get(url);
    return response.data;
  }

  async updateSuggestion(id: number, data: {
    status: string;
    completion_notes?: string;
  }): Promise<{ status: string; id: number }> {
    const response = await api.put(`/suggestions/${id}`, data);
    return response.data;
  }

  async deleteSuggestion(id: number): Promise<{ status: string; id: number }> {
    const response = await api.delete(`/suggestions/${id}`);
    return response.data;
  }

  async getRecentCompletedSuggestions(limit: number = 5, userEmail?: string, signal?: AbortSignal): Promise<{
    improvements: CompletedImprovement[];
    count: number;
  }> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (userEmail) params.append('user_email', userEmail);
    const response = await api.get(`/suggestions/completed/recent?${params.toString()}`, { signal });
    return response.data;
  }
}

const aiService = new AIService();
export default aiService;
